import { t } from "i18next";
import { Spinner } from "@thetreep/ui";
import { useEffect, useState } from "react";
import { Agency } from "@gen/agency/v1/agency_pb";
import { getAgencyInfo, getCaisse, getCaisseIds } from "@services/caisse";
import { Caisse, ListIdentitiesResponse } from "@gen/caisse/v1/caisse_pb";
import { ContactMethods } from "./ContactMethods";
import { useLocation } from "react-router";
import { getDAP } from "@services/dap";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import Select from "@components/common/Select";
import { useChatStore } from "@stores/chatStore";

export function Contact() {
  const [, setAgencies] = useState<Agency[]>([]);
  const [caisseIds, setCaisseIds] = useState<ListIdentitiesResponse>();
  const [caisseIdDAP, setCaisseIdDAP] = useState<Caisse[]>([]);
  const [showContactMethods, setShowContactMethods] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumberAgency, setPhoneNumberAgency] = useState<
    string | undefined
  >("");
  const [emailAgency, setEmailAgency] = useState<string | undefined>("");
  const [nameAgency, setNameAgency] = useState<string | undefined>("");
  const [openingHours, setOpeningHours] = useState<string | undefined>(
    undefined,
  );
  const [selectedCaisseId, setSelectedCaisseId] = useState<string | undefined>(
    undefined,
  );
  const [selectedCaisseIdDAP] = useState<string | undefined>(undefined);

  const location = useLocation();
  const { module: conversationalModule, setModule: setConversationalModule } =
    useChatStore();
  const { addToast } = useToaster();
  const [isConnected, setIsConnected] = useState(true);
  const [isLoadingAgency, setIsLoadingAgency] = useState(false);
  useEffect(() => {
    const dap = location.state?.dap;
    const name = location.state?.name;

    if (dap) {
      getDAP(dap, name)
        .then(async (response) => {
          setConversationalModule(response.conversationalModule);
          const caisseId = response.dap?.caisseId;
          const caisse = await getCaisse(caisseId ?? 0);
          setCaisseIdDAP(caisse ? [caisse] : []);
          caisse?.id.toString();
          const agencies = await getAgencyInfo(Number(caisse?.id));

          if (agencies.result.case === "error") {
            setError(agencies.result.value);
          } else {
            setError(undefined);
            setAgencies(
              Array.isArray(agencies.result.value) ? agencies.result.value : [],
            );
            setPhoneNumberAgency(agencies.result.value?.contactPhone);
            setEmailAgency(agencies.result.value?.contactEmail);
            setNameAgency(agencies.result.value?.alias);
            setOpeningHours(agencies.result.value?.openingHours);
            setShowContactMethods(agencies.result.value != null);
          }
        })
        .catch((error) => {
          console.error("Error fetching DAP or agency info:", error);
        });
    } else {
      (async () => {
        try {
          const ids = await getCaisseIds();
          setCaisseIds(ids);
        } catch (e) {
          if (e instanceof ConnectError && e.code === 16) {
            setIsConnected(false);
          } else {
            setError(t("contact.error"));
            addToast("error", t("genericError"));
          }
        }
      })();
    }
  }, []);

  const [prevCaisseIds, setPrevCaisseIds] = useState(caisseIds);
  if (caisseIds !== prevCaisseIds) {
    setPrevCaisseIds(caisseIds);
    if (caisseIds?.identities.length == 0 && !selectedCaisseId) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }

  async function handleChange(
    val: string | (string | undefined)[] | undefined,
  ) {
    setError(undefined);
    setSelectedCaisseId(val as string);
    setShowContactMethods(false);
    setPhoneNumberAgency("");
    setEmailAgency("");
    setNameAgency("");
    setOpeningHours("");
    setIsLoadingAgency(true);

    try {
      const response = await getAgencyInfo(Number(val));

      if (response.result.case === "error") {
        setError(response.result.value);
        setConversationalModule(undefined);
      } else {
        setAgencies(
          Array.isArray(response.result.value) ? response.result.value : [],
        );
        setPhoneNumberAgency(response.result.value?.contactPhone);
        setEmailAgency(response.result.value?.contactEmail);
        setNameAgency(response.result.value?.alias);
        setOpeningHours(response.result.value?.openingHours);
        setConversationalModule(response.result.value?.conversationalModule);
        setShowContactMethods(response.result.value != null);
      }
    } catch (e) {
      setError(t("contact.error"));
      setConversationalModule(undefined);
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setTimeout(() => {
        setIsLoadingAgency(false);
      }, 400); // avoid flickering
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className={`w-full flex flex-col justify-center `}>
      <div className="w-full max-w-[936px] mx-auto px-5 lg:px-0 py-8 lg:py-16 flex flex-col gap-10 ">
        <div className="flex flex-col gap-4">
          <h1 className="text-[32px] lg:text-[40px] font-jakarta text-blue-medium">
            {t("contact.title")}
          </h1>
          {isConnected ? (
            <p className="text-text-strong text-[14px] lg:text-[24px] font-jakarta">
              {t("contact.description")} <br /> {t("contact.help")}
            </p>
          ) : (
            <p className="text-text-strong text-[14px] lg:text-[24px] font-jakarta">
              {t("contact.noConnected")}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {caisseIds && isConnected && (
            <>
              <span className="text-xs font-bold">
                {t("contact.selectCaisse")}
              </span>
              <Select
                options={(caisseIds?.identities ?? []).map((caisse) => ({
                  value: caisse.id.toString(),
                  label: caisse.name,
                }))}
                value={selectedCaisseId}
                onChange={handleChange}
                placeholder={
                  !selectedCaisseId ? t("selectPlaceholder") : nameAgency
                }
                search
              />
            </>
          )}
          {caisseIdDAP.length > 0 && (
            <Select
              options={(caisseIdDAP ?? []).map((caisse) => ({
                value: caisse.id.toString(),
                label: caisse.name,
              }))}
              value={selectedCaisseIdDAP}
              onChange={handleChange}
              placeholder={nameAgency}
              search
            />
          )}
        </div>
        <div className="flex gap-2 mb-10">
          {isLoadingAgency ? (
            <div className="w-full h-full flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              {showContactMethods && !error && (
                <ContactMethods
                  phoneNumber={phoneNumberAgency ?? ""}
                  email={emailAgency ?? ""}
                  agencyName={nameAgency ?? ""}
                  openHours={openingHours ?? ""}
                  isChatEnabled={conversationalModule !== undefined}
                />
              )}
              {error && (
                <p className="font-roboto text-sm">{t("contact.error")}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
